import {Injectable, Injector} from "@angular/core";
import {DX_CORE_CONFIG, DxCoreConfig} from "../config/dx-core-config";
import {DxAppError} from "../domain/error/dx-app-error";


/**
 *  Сервис предоставляет унифицированный доступ к настройкам ядра.
 *  Создан для инкапсуляции механизмом хранения и получения конфигурации, и предоставляет унифицированный интерфейс для их получения.
 *  Изменение структуры хранения настроек не должно затрагивать остальное приложение.
 *  @publicApi
 */
@Injectable()
export class DxConfigService {
  /**
   * Сетевой протокол по-умолчанию.
   * @name DEFAULT_HTTP_PROTOCOL
   * @type {string}
   * @public
   * @readonly
   */
  public static readonly DEFAULT_HTTP_PROTOCOL: string = 'http';

  /**
   * IP-адрес приложения по-умолчанию.
   * @name DEFAULT_HOST
   * @type {string}
   * @public
   * @readonly
   */
  public static readonly DEFAULT_HOST: string = '127.0.0.1';

  /**
   * Порт приложения по-умолчанию.
   * @name DEFAULT_PORT
   * @type {string}
   * @public
   * @readonly
   */
  public static readonly DEFAULT_PORT: string = '4200';

  /**
   * Базовый url приложения по-умолчанию.
   * @name DEFAULT_BASE_URL
   * @type {string}
   * @public
   * @readonly
   */
  public static readonly DEFAULT_BASE_URL: string = '';

  /**
   * Переданный инстанс конфигурации ядра.
   * @name _config
   * @type {DxCoreConfig}
   * @readonly
   */
  protected readonly _config: DxCoreConfig;

  /**
   * Текущая стреда, на которую собрано приложение.
   * @name _env
   * @type {any}
   * @readonly
   */
  protected readonly _env: any;


  /**
   * Создает инстанс сервиса.
   * @constructor
   * @param {Injector} _injector - ссылка на корневой инжектор приложения.
   */
  // TODO: Понять почему не работает такой способ инжектирования:
  // TODO: @Inject(DX_CORE_CONFIG_PROVIDER)
  constructor(protected _injector: Injector) {
    this._config = this._injector.get(DX_CORE_CONFIG);
    this._env = this._config.env;
  }


  /**
   * Возвращает тип протокола сетевого общения: http\https.
   * @return {string} - тип протокола.
   * @defaultValue - `'http'`.
   * @public
   */
  public get httpProtocol(): string {
    return this._env.protocol ? this._env.protocol : DxConfigService.DEFAULT_HTTP_PROTOCOL;
  }


  /**
   * Возвращает ip-адрес, на который развернуто приложение.
   * @return {string} - ip-адрес.
   * @defaultValue - `'127.0.0.1'`.
   * @public
   */
  public get host(): string {
    return this._env.host ? this._env.host : DxConfigService.DEFAULT_HOST;
  }


  /**
   * Возвращает порт, на котором доступно приложение.
   * @return {string} - порт.
   * @defaultValue - `'4200'`.
   * @public
   */
  public get port(): string {
    return this._env.port ? this._env.port : DxConfigService.DEFAULT_PORT;
  }


  /**
   * Возвращает корневой для всех http-запросов, воспринимаемых бэкэндом, сегмент url.
   * @example
   * Например, для `'localhost:8080\my-api-root\...'` это будет `'my-api-root'`.
   * @return {string} - корневой для всех http-запросов, воспринимаемых бэкэндом, сегмент url.
   * @defaultValue - пустая строка.
   * @public
   */
  public get backendApiRoot(): string {
    return this._env.backendApiRoot ? this._env.backendApiRoot : '';
  }


  /**
   * Возвращает базовый сегмет url для адресации в приложении.
   * @example
   * ВНИМАНИЕ!!! Не путать с `base-href`.
   * `base-href` - это сегмент url, который проставляется ДО того места где начинает работать роутинг.
   * Например, если на томкате приложение лежит в папке `my-app`, то `base-href` должен быть равен `my-app` (проставляется при билде в `index.html`).
   * Иначе приложение в принципе не откроется. В итоге имеем url: `'localhost:8080\my-app\#'`.
   *
   * `baseUrl` - это базовый сегмент url для роутера приложения.
   * Например, при применении `HashLocationStrategy` это первый сегмент после `#`: `'localhost:8080\my-app\#\go'`.
   * Роутер будет добавлять сегменты для роутов после `go`.
   *
   * ВНИМАНИЕ!!! ОЧЕНЬ ВАЖНЫЙ МОМЕНТ!!!
   * Информация, необходимая для того чтобы выставить BASE_HREF должна быть известна на момент создания сервиса, так как
   * Провайдер APP_INITIALIZER действительно блокирует все процессы до момента окончания выполнения loadAppConfig, но НЕ блокирует вызов фабрики у провайдера APP_BASE_HREF!!!
   * Другими словами extractBaseUrl может быть вызван перед loadAppConfig, или вызов loadAppConfig произойдет первым, но extractBaseUrl будет вызван до окончания загрузки конфигов!!!
   *
   * @return {string} - базовый сегмет url для адресации в приложении.
   * @defaultValue - пустая строка.
   * @public
   */
  public get baseUrl(): string {
    return this._env.baseUrl ? this._env.baseUrl : DxConfigService.DEFAULT_BASE_URL;
  }


  /**
   * Возвращает флаг использовать\не использовать аунтфикацию.
   * @return {boolean} - флаг использовать\не использовать аунтфикацию.
   * @defaultValue - `false`.
   * @public
   */
  public get useAuth(): boolean {
    return this._env.useAuth;
  }


  /**
   * Возвращает уровень логгирования приложения.
   * @return {string} - уровень логгирования приложения.
   * @defaultValue - `'DEBUG'`.
   * @public
   */
  public get loggerLevel(): string {
    return this._env.loggerLevel ? this._env.loggerLevel : 'DEBUG';
  }


  /**
   * Возвращает флаг логгировать\не логгировать конвертацию объектов в `DxObjectMapper`.
   * @return {boolean} - флаг логгировать\не логгировать конвертацию объектов в `DxObjectMapper`.
   * @defaultValue - `false`.
   * @public
   */
  public get logObjectMapping(): string {
    return this._env.logObjectMapping;
  }


  /**
   * Возвращает флаг использовать\не использовать фэйковый бэкенд.
   * @return {boolean} - флаг использовать\не использовать фэйковый бэкенд.
   * @defaultValue - `false`.
   * @public
   */
  public get useInMemoryBackend(): boolean {
    return this._env.useInMemoryBackend;
  }


  /**
   * Возвращает имя роута для возврата в систему после перехода на аварийную страницу.
   * @return {string} - имя роута для возврата в систему после перехода на аварийную страницу.
   * @public
   */
  public get goBackRoute(): string {
    return this._config.goBackRoute;
  }


  /**
   * Возвращает имя роута для перехода на аварийную страницу.
   * @return {string} - имя роута для перехода на аварийную страницу.
   * @public
   */
  public get errorRoute(): string {
    return this._config.errorRoute;
  }

  // Адрес сервера keycloak
  public get keycloakServerUrl(): string {
    return this._env.keycloak.serverUrl;
  }

  public get keycloakRealm(): string {
    return this._env.keycloak.realm;
  }

  public get keycloakClientId(): string {
    return this._env.keycloak.clientId;
  }

  /**
   *   Метод предназначен для асинхронной загрузки конфигурации приложения во время его инициализации.
   *   Например, с сервера или статичный `json`-файлов. Для реальной блокировки процесса инициализации
   *   необходимо чтобы метод возвращал инстанс `Promise<boolean>`, который резолвится при успешном \ неуспешном завершении
   *   ВСЕХ асинхронных операций по загрузке конфигурации приложения.
   *   @return `Promise<boolean>` - для блокировки загрузки приложения должен именно промис.
   *   @public
   */
  public loadAppConfig(): Promise<boolean> {
    const error: DxAppError = new DxAppError();
    error.message = 'Функциональность метода DxConfigService.loadAppConfig(...) не реализована!';
    throw error;
  }
}
